/* eslint-disable no-nested-ternary */
import {
  Alert,
  Box, CircularProgress, Container, FormLabel, Link,
  MenuItem,
  Snackbar, TextField, Typography, useTheme,
} from '@mui/material';
import { unwrapResult } from '@reduxjs/toolkit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { t } from 'i18next';
import { CustomerSupportFormInput } from '@optimus/models';
import { StyledSelect, styledSelectMenuProps } from './StyledFormInputs';
import { useLogo } from '../config/useLogo';
import { useSolutionOrg } from '../config/useSolutionOrg';
import OptimusButton from './library/Button/Button';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { submitCustomerSupportForm } from '../store/thunks/authenticationThunk';
import NavHeader from './NavHeader';
import { SolutionOrgList } from './enums';
import { ValidFieldPattern } from '../components/Onboarding/constants';
import { selectMemberInformation } from '../store/slices/onboardingSlice';
import { isValidPhoneNumber } from './utils/formUtils';

interface IFormInputs {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  solutionOrg: string;
  description: string;
}

type ErrorProps = {
  errorMessage?: string;
};

interface ICustomerSupportFormProps {
  isDashboardSupportPage?: boolean;
}

export const ErrorAlert = ({ errorMessage }: ErrorProps) => {
  const theme = useTheme();
  return (
    <Alert
      icon={false}
      sx={{
        fontSize: theme.spacing(1.75),
        background: theme.palette.common.white,
        color: theme.palette.error.main,
        p: 0,
        '& .MuiAlert-message': {
          padding: `${theme.spacing(0)} ! important`,
        },
      }}
      severity="error"
    >
      {errorMessage || ' This field is required'}
    </Alert>
  );
};

export default function CustomerSupportForm({
  isDashboardSupportPage = false,
}: ICustomerSupportFormProps) {
  const theme = useTheme();
  const { secondaryOne: Logo } = useLogo();

  const inputStyle = () => ({
    width: '100%',
    mt: theme.spacing(0.5),
    borderRadius: theme.spacing(1),
    '& .MuiInputBase-root': {
      borderRadius: theme.spacing(1),
    },
    '& .MuiOutlinedInput-root.Mui-focused': {
      '& > fieldset': {
        borderColor: theme.palette.brand.primary,
      },
    },
  } as const);

  const labelStyle = () => ({
    color: theme.palette.grey[600],
  } as const);
  const {
    envSolutionOrgTitle, solutionOrgName, productQuestion,
  } = useSolutionOrg();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const solutionOrg = searchParams.get('solutionOrg');
  const memberInformation = useAppSelector(selectMemberInformation);
  const [isLoading, setIsLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
  } = useForm<IFormInputs>({ mode: 'onChange' });

  const openLoginForm = () => {
    if (solutionOrg) {
      navigate(`/login?solutionOrg=${solutionOrg}`);
    } else {
      navigate('/login');
    }
  };

  const handleClose = () => {
    setOpenErrorSnackbar(false);
  };

  const handleOnChange = () => {
    clearErrors('solutionOrg');
  };

  const onSubmit: SubmitHandler<IFormInputs> = (data) => {
    if (!formSubmitted) {
      setIsLoading(true);
      if (data) {
        const request: CustomerSupportFormInput = {
          firstName: isDashboardSupportPage ? memberInformation?.firstName || '' : data.firstName,
          lastName: isDashboardSupportPage ? memberInformation?.lastName || '' : data.lastName,
          email: data.email,
          phoneNumber: data.phoneNumber,
          description: data.description,
          solutionOrg:
            (isDashboardSupportPage || !productQuestion) ? solutionOrgName as string :
              data.solutionOrg || solutionOrg,
          isLoggedIn: isDashboardSupportPage,
        };
        dispatch(submitCustomerSupportForm(request))
          .then(unwrapResult)
          .then(() => {
            setOpenErrorSnackbar(false);
            setIsLoading(false);
            setFormSubmitted(true);
          })
          .catch((err: unknown) => {
            setOpenErrorSnackbar(true);
            setIsLoading(false);
          });
      }
    } else {
      // show support form to create new ticket
      setFormSubmitted(false);
      reset();
    }
  };

  return (
    <>
      {!isDashboardSupportPage && <NavHeader />}
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: isDashboardSupportPage ? 'auto' : theme.spacing(112),
          pr: theme.spacing(3),
          pl: theme.spacing(3),
          [theme.breakpoints.down('sm')]: {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
            paddingTop: (!isDashboardSupportPage && productQuestion) ? theme.spacing(5) : 0,
          },
          maxWidth: isDashboardSupportPage ?
            theme.breakpoints.values.md :
            theme.breakpoints.values.sm,
        }}
      >
        <Snackbar
          open={openErrorSnackbar}
          autoHideDuration={2000}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Alert severity="error" sx={{ width: '100%' }}>
            {t('error.generalMessage')}
          </Alert>
        </Snackbar>
        <>
          {!isDashboardSupportPage && <Box sx={{
            width: theme.spacing(38),
            alignSelf: 'center',
            textAlign: 'center',
          }}>
            {Logo && <img src={Logo as string} alt="Logo" />}
          </Box>}
          <Box
            sx={{
              background: theme.palette.common.white,
              borderRadius: theme.spacing(1),
              [theme.breakpoints.down('sm')]: {
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
              },
              paddingLeft: theme.spacing(3.5),
              paddingRight: theme.spacing(3.5),
              paddingTop: theme.spacing(3),
              marginTop: theme.spacing(0),
              ...(isDashboardSupportPage && {
                marginBottom: theme.spacing(5),
                border: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
                boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05), 0px 0px 0px 0px rgba(0, 0, 0, 0.00), 0px 0px 0px 0px rgba(0, 0, 0, 0.00)',
              }),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: theme.spacing(1),
                margin: isDashboardSupportPage ? theme.spacing(0) : '0 auto',
              }}
            >
              {!isDashboardSupportPage &&
                <Typography variant="h4" sx={{
                  mb: theme.spacing(4),
                }}>
                  Contact Support
                </Typography>
              }
              <form onSubmit={handleSubmit(onSubmit)}
                style={{ width: isDashboardSupportPage ? '100%' : 'auto' }}
              >
                {!formSubmitted ?
                  <Box>
                    {!isDashboardSupportPage &&
                      <Box
                        sx={{
                          [theme.breakpoints.up('sm')]: {
                            display: 'flex',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            mb: theme.spacing(3),
                            [theme.breakpoints.up('sm')]: {
                              width: '50%',
                              mr: theme.spacing(2),
                            },
                          }}
                        >
                          <FormLabel sx={labelStyle}>First Name*</FormLabel>
                          <TextField
                            placeholder='First Name'
                            {...register('firstName', { required: true })}
                            fullWidth
                            sx={inputStyle}
                          />
                          {(errors.firstName) && <ErrorAlert />}
                        </Box>
                        <Box
                          sx={{
                            mb: theme.spacing(3),
                            [theme.breakpoints.up('sm')]: {
                              width: '50%',
                            },
                          }}
                        >
                          <FormLabel sx={labelStyle}>Last Name*</FormLabel>
                          <TextField
                            placeholder='Last Name'
                            {...register('lastName', { required: true })}
                            margin="normal"
                            fullWidth
                            sx={inputStyle}
                          />
                          {(errors.lastName) && <ErrorAlert />}
                        </Box>
                      </Box>
                    }
                    <Box sx={{ mb: theme.spacing(3) }}>
                      <FormLabel sx={labelStyle}>Email Address*</FormLabel>
                      <TextField
                        defaultValue={isDashboardSupportPage ? memberInformation.email : ''}
                        placeholder='Email Address'
                        {...register('email', {
                          required: true,
                          pattern: {
                            value: ValidFieldPattern.validEmailPattern,
                            message: `${t('error.invalidEmail2')}`,
                          },
                        })}
                        margin="normal"
                        fullWidth
                        sx={inputStyle}
                      />
                      {(errors.email) && <ErrorAlert
                        errorMessage={errors.email?.message} />}
                    </Box>
                    <Box sx={{ mb: theme.spacing(3) }}>
                      <FormLabel sx={labelStyle}>Phone Number</FormLabel>
                      <TextField
                        placeholder='Phone Number'
                        {...register('phoneNumber', {
                          validate: (value) => !value || isValidPhoneNumber(value) || `${t('error.invalidPhoneNumber')}`,
                        })}
                        margin="normal"
                        fullWidth
                        sx={inputStyle}
                      />
                      {(errors.phoneNumber) && <ErrorAlert
                        errorMessage={errors.phoneNumber?.message} />}
                    </Box>
                    {!isDashboardSupportPage && productQuestion as boolean &&
                      <Box sx={{ mb: theme.spacing(3) }}>
                        <FormLabel sx={labelStyle}>What product do you use?*</FormLabel>
                        <StyledSelect
                          displayEmpty
                          {...register('solutionOrg', { required: true })}
                          defaultValue=''
                          onChange={(e) => { handleOnChange(); }}
                          sx={inputStyle}
                          MenuProps={styledSelectMenuProps}
                        >
                          <MenuItem disabled value=''>
                            Please select an option
                          </MenuItem>
                          {SolutionOrgList.map((option) => (
                            <MenuItem
                              key={option.value}
                              value={option.value}
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </StyledSelect>
                        {(errors.solutionOrg) && <ErrorAlert />}
                      </Box>
                    }
                    <Box>
                      <FormLabel sx={labelStyle}>Description*</FormLabel>
                      <TextField
                        placeholder='Please describe the issue you are facing'
                        {...register('description', { required: true })}
                        margin="normal"
                        fullWidth
                        multiline
                        rows={4}
                        sx={inputStyle}
                      />
                      {(errors.description) && <ErrorAlert />}
                    </Box>
                  </Box> : <Alert icon={<CheckCircleIcon />}
                    sx={{ bgcolor: theme.palette.common.white }}
                  >
                    {t('success.supportTicketSubmitted')}
                  </Alert>
                }
                <OptimusButton
                  buttonText={isLoading ?
                    <CircularProgress color="inherit" size={24} /> :
                    (formSubmitted ? `${t('button.createNewTicket')}` :
                      `${t('button.submitSupportTicket')}`)}
                  customVariant="primary"
                  size="m"
                  fullWidth={false}
                  type='submit'
                  disabled={isLoading}
                  sx={{ marginTop: theme.spacing(3), marginBottom: theme.spacing(4) }}
                />
              </form>
            </Box>
          </Box>
          {!isDashboardSupportPage && <Box
            sx={{
              mt: theme.spacing(3),
              textAlign: 'center',
            }}
          >
            <Typography variant='body7'>{t('supportForm.or')} &nbsp;
              <Link onClick={() => openLoginForm()}
                sx={{
                  cursor: 'pointer',
                }}
                variant='h6'>
                {`${t('supportForm.backToLogin')} ${envSolutionOrgTitle}`}</Link>
            </Typography>
          </Box>}
        </>
      </Container>
    </>
  );
}
